import type {
  IAccountRepository,
  IAccountTransferRepository,
  ICategoryRepository,
  ICreditorRepository,
  ICreditorPaymentRepository,
  IDebtorRepository,
  IDebtorPaymentRepository,
  ICurrencyRepository,
  ICurrencyExchangeRepository,
  IExpenseRepository,
  IExternalTransactionRepository,
  IIncomeRepository,
  ICurrencyRateRepository,
  IPlannedExpenseRepository,
  IPlannedIncomeRepository,
  IReportingRepository,
  ITagRepository,
  ITransactionManager,
} from '@app/domain/ports';

import {
  AccountRepository,
  AccountTransferRepository,
  CategoryRepository,
  CreditorRepository,
  CreditorPaymentRepository,
  DebtorRepository,
  DebtorPaymentRepository,
  CurrencyRepository,
  CurrencyExchangeRepository,
  ExpenseRepository,
  ExternalTransactionRepository,
  IncomeRepository,
  CurrencyRateRepository,
  PlannedExpenseRepository,
  PlannedIncomeRepository,
  ReportingRepository,
  TagRepository,
} from '../repositories';
import { SqliteProvider } from '../providers/sqlite.provider';
import { SqliteTransactionManager } from '../services/transaction-manager';

/**
 * Factory for creating SQLite-based repositories.
 * Handles all repositories that depend on SQLite storage.
 */
export class SqliteRepositoryFactory {
  constructor(public readonly provider: SqliteProvider) {}

  createAccountRepository(): IAccountRepository {
    return new AccountRepository(this.provider);
  }

  createAccountTransferRepository(): IAccountTransferRepository {
    return new AccountTransferRepository(this.provider);
  }

  createCategoryRepository(): ICategoryRepository {
    return new CategoryRepository(this.provider);
  }

  createCreditorRepository(): ICreditorRepository {
    return new CreditorRepository(this.provider);
  }

  createCreditorPaymentRepository(): ICreditorPaymentRepository {
    return new CreditorPaymentRepository(this.provider);
  }

  createDebtorRepository(): IDebtorRepository {
    return new DebtorRepository(this.provider);
  }

  createDebtorPaymentRepository(): IDebtorPaymentRepository {
    return new DebtorPaymentRepository(this.provider);
  }

  createCurrencyRepository(): ICurrencyRepository {
    return new CurrencyRepository(this.provider);
  }

  createCurrencyExchangeRepository(): ICurrencyExchangeRepository {
    return new CurrencyExchangeRepository(this.provider);
  }

  createTransactionManager(): ITransactionManager {
    return new SqliteTransactionManager(this.provider);
  }

  createExpenseRepository(): IExpenseRepository {
    return new ExpenseRepository(this.provider);
  }

  createPlannedExpenseRepository(): IPlannedExpenseRepository {
    return new PlannedExpenseRepository(this.provider);
  }

  createPlannedIncomeRepository(): IPlannedIncomeRepository {
    return new PlannedIncomeRepository(this.provider);
  }

  createExternalTransactionRepository(): IExternalTransactionRepository {
    return new ExternalTransactionRepository(this.provider);
  }

  createIncomeRepository(): IIncomeRepository {
    return new IncomeRepository(this.provider);
  }

  createCurrencyRateRepository(): ICurrencyRateRepository {
    return new CurrencyRateRepository(this.provider);
  }

  createReportingRepository(): IReportingRepository {
    return new ReportingRepository(this.provider);
  }

  createTagRepository(): ITagRepository {
    return new TagRepository(this.provider);
  }
}
